const EARTH_RADIUS_KM = 6371.0088;

export const haversineDistanceKm = (coord0, coord1) => {
  if (!coord0 || !coord1) return 0;
  const [lon1, lat1] = coord0;
  const [lon2, lat2] = coord1;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const rLat1 = (lat1 * Math.PI) / 180;
  const rLat2 = (lat2 * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(rLat1) * Math.cos(rLat2) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_KM * c;
};

export const computeSegmentDistancesKm = (waypoints = []) => {
  if (!Array.isArray(waypoints) || waypoints.length < 2) {
    return [];
  }

  const distances = [];
  for (let i = 0; i < waypoints.length - 1; i++) {
    const p0 = waypoints[i]?.coordinate;
    const p1 = waypoints[i + 1]?.coordinate;
    distances.push(haversineDistanceKm(p0, p1));
  }
  return distances;
};

const computeTravelledKm = (segments, totalKm, progress) => {
  const count = segments.length;
  if (count === 0) return 0;
  const clampedP = Math.max(0, Math.min(1, progress));
  if (clampedP >= 1) return totalKm;

  const rawIndex = clampedP * count;
  const activeSegIdx = Math.min(count - 1, Math.floor(rawIndex));
  const localT = rawIndex - activeSegIdx;

  let completedSum = 0;
  for (let i = 0; i < activeSegIdx; i++) {
    completedSum += segments[i];
  }
  return completedSum + segments[activeSegIdx] * localT;
};

export const computeDistanceStats = (waypoints = [], progress = 0, distanceUnit = "km") => {
  const segments = computeSegmentDistancesKm(waypoints);
  const totalKm = segments.reduce((sum, d) => sum + d, 0);
  const travelledKm = computeTravelledKm(segments, totalKm, progress);

  if (distanceUnit === "nm") {
    return {
      totalDistance: totalKm / 1.852,
      travelledDistance: travelledKm / 1.852,
      unitLabel: "nm",
    };
  }

  return {
    totalDistance: totalKm,
    travelledDistance: travelledKm,
    unitLabel: "km",
  };
};

export const computeArcHeight = (routeStyle = "flight", distanceKm = 0) => {
  switch (routeStyle) {
    case "direct":
      return 0;
    case "trek":
      return Math.min(0.45, Math.max(0.15, distanceKm / 1800 + 0.1));
    case "sea":
      return Math.min(1.1, Math.max(0.35, distanceKm / 1000 + 0.25));
    case "flight":
    default:
      return Math.min(2.4, Math.max(0.9, distanceKm / 550 + 0.6));
  }
};

export const computeParabolicZoom = ({
  startZoom = 8,
  endZoom = 8,
  localT = 0,
  distanceKm = 0,
  routeStyle = "flight",
  prefersReducedMotion = false,
}) => {
  const clampedT = Math.max(0, Math.min(1, localT));
  const smoothT = clampedT * clampedT * (3 - 2 * clampedT);
  const linearZoom = startZoom + (endZoom - startZoom) * smoothT;

  if (prefersReducedMotion || routeStyle === "direct") {
    return linearZoom;
  }

  const arcHeight = computeArcHeight(routeStyle, distanceKm);
  const smoothBell = Math.pow(Math.sin(clampedT * Math.PI), 2);
  const deltaZ = smoothBell * arcHeight;

  return Math.max(2.5, linearZoom - deltaZ);
};

export const computeCameraState = ({
  waypoints = [],
  progress = 0,
  activeStepIndex,
  baseZoom = 8,
  routeStyle = "flight",
  prefersReducedMotion = false,
}) => {
  if (!Array.isArray(waypoints) || waypoints.length === 0) {
    return {
      centerLonLat: [10.46, 61.11],
      zoom: baseZoom,
      activeWaypointIndex: 0,
      segmentIndex: 0,
      localT: 0,
    };
  }

  if (waypoints.length === 1) {
    return {
      centerLonLat: waypoints[0].coordinate,
      zoom: waypoints[0].zoom || baseZoom,
      activeWaypointIndex: 0,
      segmentIndex: 0,
      localT: 0,
    };
  }

  const segmentCount = waypoints.length - 1;
  const clampedP = Math.max(0, Math.min(1, progress));

  let segmentIndex = 0;
  let localT = 0;

  if (clampedP >= 1) {
    segmentIndex = segmentCount - 1;
    localT = 1;
  } else {
    const rawIndex = clampedP * segmentCount;
    segmentIndex = Math.min(segmentCount - 1, Math.floor(rawIndex));
    localT = rawIndex - segmentIndex;
  }

  const p0 = waypoints[segmentIndex].coordinate;
  const p1 = waypoints[segmentIndex + 1].coordinate;

  const centerLonLat = [
    p0[0] + (p1[0] - p0[0]) * localT,
    p0[1] + (p1[1] - p0[1]) * localT,
  ];

  const startZoom = waypoints[segmentIndex].zoom || baseZoom;
  const endZoom = waypoints[segmentIndex + 1].zoom || baseZoom;
  const distanceKm = haversineDistanceKm(p0, p1);

  const zoom = computeParabolicZoom({
    startZoom,
    endZoom,
    localT,
    distanceKm,
    routeStyle,
    prefersReducedMotion,
  });

  const progressWaypointIndex = localT < 0.5 ? segmentIndex : segmentIndex + 1;
  let activeWaypointIndex;
  if (progress > 0) {
    activeWaypointIndex = progressWaypointIndex;
  } else if (typeof activeStepIndex === "number" && activeStepIndex >= 0 && activeStepIndex < waypoints.length) {
    activeWaypointIndex = activeStepIndex;
  } else {
    activeWaypointIndex = progressWaypointIndex;
  }

  return {
    centerLonLat,
    zoom,
    activeWaypointIndex,
    segmentIndex,
    localT,
  };
};
